import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    MenuItem,
    Button,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    RadioGroup,
    FormControlLabel,
    Radio,
    IconButton,
    Chip
} from '@mui/material';
import {
    Assessment as DocIcon,
    BarChart as ChartIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { frFR } from '@mui/x-date-pickers/locales';
import dayjs from 'dayjs';
import { COLORS } from '../../../settings/themeSettings';
import { ANALYSES_LIST } from './DashboardAnalysisModal';

const DashboardCombinedView = ({
    criteria,
    onCriteriaChange,
    selectedAnalysisId,
    onSelectAnalysis,
    onCloseCombined,
    onRefresh
}) => {
    // Mode de sélection de la période : 'Date' ou 'Echelonnement'
    const [periodMode, setPeriodMode] = useState(
        criteria.echelonnement && criteria.echelonnement.toLowerCase() !== 'date' ? 'Echelonnement' : 'Date'
    );

    const isDateMode = periodMode === 'Date';

    const handlePeriodModeChange = (mode) => {
        setPeriodMode(mode);
        if (mode === 'Date') {
            onCriteriaChange({ ...criteria, echelonnement: 'Date' });
        } else {
            onCriteriaChange({ 
                ...criteria, 
                echelonnement: criteria.echelonnement === 'Date' ? 'Mois' : (criteria.echelonnement || 'Mois') 
            });
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}>
            <Paper 
                elevation={0}
                sx={{ 
                    p: 2.5, 
                    mb: 3, 
                    border: '2px solid #0f172a', 
                    borderRadius: 2, 
                    bgcolor: '#ffffff',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
            >
                {/* En-tête officiel Écran 3 */}
                <Box sx={{ 
                    bgcolor: '#0f172a', 
                    color: 'white', 
                    p: 1.5, 
                    mx: -2.5, 
                    mt: -2.5, 
                    mb: 2.5,
                    borderRadius: '6px 6px 0 0',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            Tableau de bord personnalisé - complet
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            Critères de sélection et choix d'analyses avec recalcul en temps réel
                        </Typography>
                    </Box>
                    <IconButton onClick={onCloseCombined} size="small" sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Grid container spacing={3}>
                    {/* ═════════════════════════════════════════════════════════ */}
                    {/* PANNEAU GAUCHE : CRITÈRES DE SÉLECTION (ÉCRAN 1)          */}
                    {/* ═════════════════════════════════════════════════════════ */}
                    <Grid item xs={12} md={5}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc', height: '100%' }}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary.main" sx={{ mb: 1.5 }}>
                                Critères de recalcul en direct
                            </Typography>

                            {/* Choix du mode Période : Date vs Echelonnement */}
                            <RadioGroup 
                                row 
                                value={periodMode} 
                                onChange={(e) => handlePeriodModeChange(e.target.value)}
                                sx={{ mb: 1.5 }}
                            >
                                <FormControlLabel 
                                    value="Date" 
                                    control={<Radio size="small" />} 
                                    label={<Typography variant="body2" fontWeight="600">Mode Date</Typography>} 
                                />
                                <FormControlLabel 
                                    value="Echelonnement" 
                                    control={<Radio size="small" />} 
                                    label={<Typography variant="body2" fontWeight="600">Mode Échelonnement</Typography>} 
                                />
                            </RadioGroup>

                            {/* Dates De / À (actives uniquement si mode Date) */}
                            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <DatePicker
                                        label="De"
                                        disabled={!isDateMode}
                                        value={criteria.startDate ? dayjs(criteria.startDate) : null}
                                        onChange={(newVal) => {
                                            onCriteriaChange({
                                                ...criteria,
                                                startDate: newVal ? newVal.format('YYYY-MM-DD') : ''
                                            });
                                        }}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <DatePicker
                                        label="À"
                                        disabled={!isDateMode}
                                        value={criteria.endDate ? dayjs(criteria.endDate) : null}
                                        onChange={(newVal) => {
                                            onCriteriaChange({
                                                ...criteria,
                                                endDate: newVal ? newVal.format('YYYY-MM-DD') : ''
                                            });
                                        }}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                            </Grid>

                            {/* Échelonnement (actif uniquement si mode Échelonnement) */}
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Échelonnement (Regroupement)"
                                disabled={isDateMode}
                                value={isDateMode ? 'Date' : (criteria.echelonnement || 'Mois')}
                                onChange={(e) => {
                                    onCriteriaChange({
                                        ...criteria,
                                        echelonnement: e.target.value
                                    });
                                }}
                                sx={{ mb: 2 }}
                            >
                                <MenuItem value="Date">Date</MenuItem>
                                <MenuItem value="Mois">Mois</MenuItem>
                                <MenuItem value="Trimestre">Trimestre</MenuItem>
                                <MenuItem value="Semestre">Semestre</MenuItem>
                            </TextField>

                            {/* Type d'activité */}
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Activité"
                                value={criteria.typeActivite || 'Tous'}
                                onChange={(e) => {
                                    onCriteriaChange({
                                        ...criteria,
                                        typeActivite: e.target.value
                                    });
                                }}
                                sx={{ mb: 2 }}
                            >
                                <MenuItem value="Tous">Tous (par défaut)</MenuItem>
                                <MenuItem value="Billetterie">Billetterie</MenuItem>
                                <MenuItem value="Tourisme">Tourisme</MenuItem>
                            </TextField>

                            <Box sx={{ mt: 'auto', pt: 1, display: 'flex', gap: 1.5 }}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    fullWidth
                                    startIcon={<RefreshIcon />}
                                    onClick={onRefresh}
                                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                                >
                                    OK (Recalculer)
                                </Button>
                                <Button 
                                    variant="outlined" 
                                    color="inherit" 
                                    onClick={onCloseCombined}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Fermer
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* ═════════════════════════════════════════════════════════ */}
                    {/* PANNEAU DROIT : SÉLECTION DE L'ANALYSE (ÉCRAN 2)          */}
                    {/* ═════════════════════════════════════════════════════════ */}
                    <Grid item xs={12} md={7}>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{ p: 1.5, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
                                    Fichier d'analyse à afficher
                                </Typography>
                            </Box>

                            <List disablePadding>
                                {ANALYSES_LIST.map((item, idx) => {
                                    const isSelected = selectedAnalysisId === item.id;
                                    const isChart = item.id === 'graphique';

                                    return (
                                        <ListItemButton
                                            key={item.id}
                                            selected={isSelected}
                                            onClick={() => onSelectAnalysis(item.id)}
                                            sx={{
                                                py: 1.2,
                                                px: 2,
                                                borderBottom: idx < ANALYSES_LIST.length - 1 ? '1px solid #f1f5f9' : 'none',
                                                bgcolor: isSelected ? '#edf3fc !important' : 'transparent',
                                                borderLeft: isSelected ? `4px solid ${COLORS.primary || '#0ea5e9'} !important` : '4px solid transparent',
                                                '&:hover': { bgcolor: '#f8fafc' }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36, color: isSelected ? (COLORS.primary || '#0ea5e9') : '#64748b' }}>
                                                {isChart ? <ChartIcon fontSize="small" /> : <DocIcon fontSize="small" />}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2" fontWeight={isSelected ? 700 : 500} color={isSelected ? 'primary.main' : 'text.primary'}>
                                                        {item.title}
                                                    </Typography>
                                                }
                                            />
                                            <Chip 
                                                label={item.code} 
                                                size="small" 
                                                color={isSelected ? 'primary' : 'default'} 
                                                variant={isSelected ? 'filled' : 'outlined'}
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
            </Paper>
        </LocalizationProvider>
    );
};

export default DashboardCombinedView;
